/**
 * Which of the patient's other studies the viewer opens as priors.
 *
 * `/patient/get` returns the patient's studies newest first, as many as the
 * server was willing to send: `viewerMaxPriorCount`, or more when one of the
 * caller's protocols asks for it (see HpPriorPolicyService on the server — it
 * takes the largest count any protocol could want, since it does not know which
 * one will win). Narrowing that to the protocol that actually applies is this
 * module's job: `priorStudyConditions` decides which studies qualify, and
 * `openMaxPriors` caps how many are opened.
 *
 * ## Why it runs on DTOs, before the viewer builds anything
 *
 * Everything downstream of `generatePriorStudyData` is positional in the
 * priors array — thumbnail offsets sum `priorStudyData[i - 1].stacks.length`,
 * prefetch walks it by index, viewports address studies by index. Dropping a
 * study after that array exists would desync all three, so selection happens on
 * the raw response and the array is built from what survives.
 *
 * The cost is that protocol matching here sees a DTO rather than a loaded
 * study, so it cannot answer a condition that needs a DICOM tag.
 *
 * ## How much that costs, and where
 *
 * Two separate judgements, deliberately kept apart:
 *
 * 1. **Is the match trustworthy?** Only the conditions the matcher actually read
 *    on its way to the winner can have changed the answer, so only those are
 *    checked against `DTO_MATCHABLE_FIELDS`. A protocol sitting after the winner
 *    is free to use whatever it likes. This matters because the document now
 *    carries the shared system protocols too — under a whole-document check, one
 *    admin protocol keyed on a DICOM tag would switch prior filtering off for
 *    every radiologist at once.
 * 2. **Can this prior be judged?** A `priorStudyConditions` group naming a field
 *    the DTO cannot answer is treated as *passing*, so the study is kept. The
 *    rule engine reads an absent field as empty, which quietly fails `contain`
 *    and would hide a prior the radiologist is expecting — the one failure worth
 *    engineering against here.
 *
 * The prior conditions themselves are evaluated per candidate study; see
 * `buildPriorContext` for what that means and what is unverified about it.
 */

import { findApplicableProtocol } from "./protocolMatcher";
import { evaluateRuleGroup } from "./ruleEngine";
import { buildPriorContext, buildStudyContext, fieldsUsed, fromStudyDto } from "./viewerContext";

/**
 * Study- and session-scope fields `fromStudyDto` can answer, and therefore the
 * only ones the matcher may have read if we are to trust the protocol it picked.
 */
export const DTO_MATCHABLE_FIELDS = new Set([
  "main_study_modality",
  "modalities_in_study",
  "study_description",
  "study_instances_count",
  "series_count",
  "series_description",
  "open_studies_count",
  "patient_studies_count",
  "patient_studies_modality_count",
  "open_studies_modality_count",
]);

/**
 * The prior-scope vocabulary, all of which `buildPriorContext` answers from a
 * DTO. A prior condition naming anything else cannot be judged here — in
 * practice that means the `other` vocabulary, which belongs to studies already
 * open rather than to the patient's history.
 */
export const PRIOR_MATCHABLE_FIELDS = new Set([
  "modalities_in_patient_study",
  "patient_study_description",
  "scroll_step",
  "study_date_filter",
]);

/** Fields of a condition tree that the prior context cannot source. */
const unanswerablePriorFields = (group) =>
  [...fieldsUsed(group)].filter((field) => !PRIOR_MATCHABLE_FIELDS.has(field));

/**
 * Which of the category's prior conditions a candidate study satisfies, 1-based.
 *
 * The index is the answer, not just the count: a viewport addresses a prior as
 * `{ conditionIndex, studyIndex }`, so "Prior study 2" has to stay
 * distinguishable from "Prior study 1" all the way to the hang.
 *
 * An entry naming a field the DTO cannot answer counts as satisfied. The list is
 * a disjunction — each entry describes one prior — and erring towards keeping a
 * study is the point: an extra thumbnail is noise, a missing one is a comparison
 * the radiologist does not know they are not being shown.
 */
function conditionsSatisfiedBy(groups, context, options) {
  const satisfied = [];
  groups.forEach((group, index) => {
    const blind = unanswerablePriorFields(group);
    if (blind.length > 0 || evaluateRuleGroup(group, context, options)) satisfied.push(index + 1);
  });
  return satisfied;
}

/**
 * How many of the matching priors to open.
 *
 * `openMaxPriors` is applied only when positive. Zero is what `createCategory`
 * starts a protocol with, so it reads as "not set" rather than "open none" —
 * taking it literally would empty the prior panel for every protocol whose
 * author never touched the field. `openAllPriors` lifts the cap, which is the
 * precedence the protocol tree already shows (all, else max).
 */
function capOf(category, matchedCount) {
  if (category?.openAllPriors) return matchedCount;
  const max = category?.openMaxPriors ?? 0;
  return max > 0 ? Math.min(max, matchedCount) : matchedCount;
}

/**
 * Choose the priors to open.
 *
 * @param document   a hangingProtocolsV2 document, or null
 * @param studies    the API's studyDtoList — the current study and the
 *                   patient's others, newest first
 * @param parentStudyId  id of the study being viewed; it is always kept
 * @returns `{ studies, category, dropped, byCondition, reason }` — `studies` in
 *          the order it arrived, `byCondition` mapping a 1-based condition index
 *          to the positions in `studies` that satisfied it, and `reason` naming
 *          why nothing was filtered when nothing was
 */
export function selectPriorStudies(document, studies, { parentStudyId = null, device = null, options = {} } = {}) {
  const all = Array.isArray(studies) ? studies : [];
  const unchanged = (reason) => ({
    studies: all,
    category: null,
    dropped: [],
    // No protocol was applied, so no viewport can address a prior through one.
    byCondition: new Map(),
    reason,
  });

  // One study is the current one, so there is nothing to select from.
  if (!document || all.length < 2) return unchanged("no-document");

  // The list is date-sorted, so the study being viewed is usually first — but
  // not when the patient has a newer one, hence the id. Compared as text: the
  // id arrives from the route as a string and from the API as a number.
  const parentIndex = Math.max(
    0,
    all.findIndex((study) => study?.id != null && String(study.id) === String(parentStudyId))
  );

  const studyContext = buildStudyContext(fromStudyDto(all[parentIndex]), {
    // What the patient has, and what would be open without filtering — both
    // describe the situation the protocol is chosen for, before we narrow it.
    openStudiesCount: all.length,
    patientStudiesCount: all.length,
  });

  // Everything the walk read on its way to the answer, so we can tell whether a
  // fuller picture of the study would have picked a different protocol.
  const consultedFields = new Set();
  const match = findApplicableProtocol(document, studyContext, { device, ...options, consultedFields });

  // Ahead of the no-match case on purpose: an unanswerable condition can be the
  // reason a protocol looked inapplicable, and "we could not tell" is the more
  // honest report than "none applied".
  const blind = [...consultedFields].filter((field) => !DTO_MATCHABLE_FIELDS.has(field));
  if (blind.length > 0) return unchanged(`match-not-sourceable-from-dto: ${blind.join(", ")}`);

  if (!match) return unchanged("no-applicable-protocol");

  const { category } = match;
  const groups = category?.priorStudyConditions ?? [];
  const candidates = all.filter((_, index) => index !== parentIndex);

  const satisfiedBy = candidates.map((study, index) =>
    conditionsSatisfiedBy(
      groups,
      // Offset counts across all candidates, not just the matching ones: it is
      // the study's distance from the current one.
      buildPriorContext(fromStudyDto(study), { offset: index + 1 }),
      options
    )
  );
  // A category with no prior conditions places no constraint, so everything
  // qualifies — but nothing is addressable by condition index.
  const qualifies = (index) => groups.length === 0 || satisfiedBy[index].length > 0;
  const matched = candidates.filter((_, index) => qualifies(index));

  const kept = new Set(matched.slice(0, capOf(category, matched.length)));
  const selected = all.filter((study, index) => index === parentIndex || kept.has(study));

  // Positions in `selected`, which is the array priorStudyData is built from —
  // so these are the indices a hang can address a study by.
  const byCondition = new Map();
  selected.forEach((study, studyIndex) => {
    const candidateIndex = candidates.indexOf(study);
    if (candidateIndex === -1) return;
    satisfiedBy[candidateIndex].forEach((conditionIndex) => {
      if (!byCondition.has(conditionIndex)) byCondition.set(conditionIndex, []);
      byCondition.get(conditionIndex).push(studyIndex);
    });
  });

  return {
    studies: selected,
    category,
    dropped: candidates.filter((study) => !kept.has(study)),
    byCondition,
    reason: null,
  };
}
